import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactFlow, {
  Background, Controls, MarkerType, useNodesState, useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { Loader2, Sparkles, RefreshCw, Share2, Network, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { TableNode } from "@/components/semantics/TableNode";
import { NodePanel } from "@/components/semantics/NodePanel";
import { EdgeDrawer } from "@/components/semantics/EdgeDrawer";
import { SemanticsTabs } from "@/components/semantics/SemanticsTabs";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useSession } from "@/context/AuthContext";
import { EDGE_KINDS, KIND_BADGE, edgeColor, canReviewRole } from "@/lib/semantics";
import { SEMANTICS } from "@/constants/testIds";
import api from "@/lib/api";

const nodeTypes = { table: TableNode };

// Above this many tables, the full ReactFlow graph is unreadable — require focus.
const SEM_FOCUS_LIMIT = 25;

function layout(nodes) {
  const bySchema = {};
  nodes.forEach((n) => { (bySchema[n.schema] ||= []).push(n); });
  const schemas = Object.keys(bySchema).sort();
  const pos = {};
  schemas.forEach((s, ci) => {
    bySchema[s].forEach((n, ri) => { pos[n.id] = { x: ci * 300, y: ri * 132 + 20 }; });
  });
  return pos;
}

export default function Semantics() {
  const { active } = useWorkspace();
  const { user } = useSession();
  const canReview = canReviewRole(user?.role);
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [activeKinds, setActiveKinds] = useState(new Set(EDGE_KINDS));
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [drawerEdge, setDrawerEdge] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [focus, setFocus] = useState("");

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.get("/semantics/graph", { params: { workspace_id: active.id } });
      setGraph(data);
    } catch {
      toast.error("Failed to load semantic graph");
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const nodeColumns = useMemo(() => {
    const m = {};
    (graph?.nodes || []).forEach((n) => { m[n.id] = n.columns.map((c) => c.name); });
    return m;
  }, [graph]);

  // Focused view: focus table + direct neighbors only. Large schemas are gated
  // until a focus is chosen so the canvas never becomes a hairball.
  const view = useMemo(() => {
    if (!graph) return { nodes: [], edges: [], gated: false };
    if (focus) {
      const ids = new Set([focus]);
      const sub = graph.edges.filter((e) => e.from_fqn === focus || e.to_fqn === focus);
      sub.forEach((e) => { ids.add(e.from_fqn); ids.add(e.to_fqn); });
      return { nodes: graph.nodes.filter((n) => ids.has(n.id)), edges: sub, gated: false };
    }
    if (graph.nodes.length > SEM_FOCUS_LIMIT) return { nodes: [], edges: [], gated: true };
    return { nodes: graph.nodes, edges: graph.edges, gated: false };
  }, [graph, focus]);

  // Build nodes from the (possibly focused) view.
  useEffect(() => {
    if (!graph) return;
    const pos = layout(view.nodes);
    setNodes(view.nodes.map((n) => ({
      id: n.id, type: "table", position: pos[n.id] || { x: 0, y: 0 },
      data: { node: n, selected: false },
    })));
  }, [graph, view, setNodes]);

  // Build edges from the view + kind filter (with join labels when focused).
  useEffect(() => {
    if (!graph) return;
    setEdges(view.edges.filter((e) => activeKinds.has(e.kind)).map((e) => {
      const color = edgeColor(e);
      const label = focus
        ? (e.from_column && e.to_column ? `${e.from_column} → ${e.to_column}  ·  ${e.confidence}` : e.kind)
        : undefined;
      return {
        id: e.id, source: e.from_fqn, target: e.to_fqn, type: "smoothstep",
        animated: e.verified,
        label,
        labelStyle: { fontSize: 9, fill: "hsl(var(--muted-foreground))", fontFamily: "monospace" },
        labelBgStyle: { fill: "hsl(var(--background))", fillOpacity: 0.8 },
        style: {
          stroke: color,
          strokeWidth: e.verified ? 2 : 1.4,
          strokeDasharray: e.status === "rejected" ? "4 4" : e.verified ? "0" : "5 4",
          opacity: e.status === "rejected" ? 0.5 : 0.9,
        },
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
        data: { edge: e },
      };
    }));
  }, [graph, view, activeKinds, focus, setEdges]);

  // Reflect selection without resetting positions.
  useEffect(() => {
    setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, selected: n.id === selectedNodeId } })));
  }, [selectedNodeId, setNodes]);

  const onNodeClick = useCallback((_e, node) => {
    setDrawerOpen(false);
    setSelectedNodeId(node.id);
  }, []);

  const onEdgeClick = useCallback((_e, edge) => {
    setDrawerEdge(edge.data.edge);
    setDrawerOpen(true);
  }, []);

  const onDiscover = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post("/semantics/metrics/discover", null, { params: { workspace_id: active.id } });
      toast.success(`Discovered ${data.count} metric${data.count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discovery failed");
    } finally {
      setDiscovering(false);
    }
  };

  const toggleKind = (k) => setActiveKinds((prev) => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });

  const selectedNode = useMemo(
    () => (graph?.nodes || []).find((n) => n.id === selectedNodeId) || null,
    [graph, selectedNodeId]
  );

  const noSource = !loading && graph && graph.nodes.length === 0;

  return (
    <div data-testid={SEMANTICS.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-7xl px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Semantic layer</h1>
              <SemanticsTabs active="graph" />
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              See and verify how tables relate. Verified joins drive accurate cross-table answers in chat.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid={SEMANTICS.refresh} onClick={load} className="rounded-full border-border/70 text-xs">
              <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh
            </Button>
            {canReview && (
              <Button size="sm" data-testid={SEMANTICS.discoverBtn} onClick={onDiscover} disabled={discovering} className="rounded-full bg-accent text-xs uppercase tracking-[0.14em] text-accent-foreground hover:bg-accent/90">
                {discovering ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />}
                Discover metrics
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading graph…</div>
        ) : noSource ? (
          <div data-testid={SEMANTICS.empty} className="mt-10 rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-16 text-center">
            <Network className="mx-auto h-8 w-8 text-accent" />
            <h2 className="mt-5 text-lg font-semibold">No tables in this workspace</h2>
            <p className="mt-2 text-sm text-muted-foreground">Connect a data source first, then the semantic graph appears here.</p>
            <Link to="/schema" className="mt-6 inline-block text-xs uppercase tracking-[0.18em] text-accent hover:underline">Go to Schema →</Link>
          </div>
        ) : (
          <>
            {/* focus control — keeps large schemas readable */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                list="sem-focus-tables"
                data-testid={SEMANTICS.focusInput}
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                placeholder="Focus a table to see only its relationships…"
                className="h-8 w-80 rounded-md border border-border/70 bg-card/60 px-2.5 text-xs text-foreground outline-none focus:border-accent"
              />
              <datalist id="sem-focus-tables">
                {(graph?.nodes || []).map((n) => <option key={n.id} value={n.id} />)}
              </datalist>
              {focus && (
                <button
                  data-testid={SEMANTICS.focusClear}
                  onClick={() => setFocus("")}
                  className="rounded-full border border-border/60 px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
                >
                  Clear focus
                </button>
              )}
              {focus && (
                <span className="text-[10px] text-muted-foreground">
                  {view.nodes.length} table(s) · join keys + confidence shown on edges
                </span>
              )}
            </div>

            {/* kind filter chips + legend */}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div data-testid={SEMANTICS.kindFilter} className="flex flex-wrap items-center gap-1.5">
                {EDGE_KINDS.map((k) => {
                  const on = activeKinds.has(k);
                  return (
                    <button
                      key={k}
                      onClick={() => toggleKind(k)}
                      data-testid={`semantics-kind-${k}`}
                      className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] transition-colors ${
                        on ? KIND_BADGE[k] : "border-border/50 text-muted-foreground/50"
                      }`}
                    >
                      {k}
                    </button>
                  );
                })}
              </div>
              <div data-testid={SEMANTICS.legend} className="flex items-center gap-4 text-[10px] text-muted-foreground">
                <span className="inline-flex items-center gap-1"><span className="inline-block h-0.5 w-5" style={{ background: "#2563eb" }} /> verified</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block h-0.5 w-5 border-t border-dashed" style={{ borderColor: "#94a3b8" }} /> inferred</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block h-0.5 w-5 border-t border-dashed" style={{ borderColor: "#ef4444" }} /> rejected</span>
              </div>
            </div>

            <div className="relative mt-4 h-[620px] overflow-hidden rounded-xl border border-border/60 bg-card/20">
              {view.gated ? (
                <div data-testid={SEMANTICS.focusHint} className="flex h-full flex-col items-center justify-center px-6 text-center">
                  <Network className="h-8 w-8 text-accent" />
                  <p className="mt-4 text-sm font-medium">{graph.nodes.length} tables in this schema</p>
                  <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                    Showing every table at once is unreadable. Use <b>Focus a table</b> above to view just that table and its direct relationships — with join keys and confidence on each edge.
                  </p>
                </div>
              ) : (
                <ReactFlow
                  data-testid={SEMANTICS.graph}
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={onNodeClick}
                  onEdgeClick={onEdgeClick}
                  onPaneClick={() => setSelectedNodeId(null)}
                  nodeTypes={nodeTypes}
                  fitView
                  fitViewOptions={{ padding: 0.2 }}
                  minZoom={0.2}
                  proOptions={{ hideAttribution: true }}
                >
                  <Background color="hsl(var(--muted-foreground))" gap={20} size={0.5} />
                  <Controls showInteractive={false} className="!border-border/60" />
                </ReactFlow>
              )}
              {selectedNode && (
                <NodePanel
                  node={selectedNode}
                  edges={graph.edges}
                  onClose={() => setSelectedNodeId(null)}
                  onSelectNode={(id) => setSelectedNodeId(id)}
                />
              )}
            </div>
          </>
        )}
      </main>

      <EdgeDrawer
        edge={drawerEdge}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        workspaceId={active?.id}
        nodeColumns={nodeColumns}
        onChanged={load}
        canReview={canReview}
      />
    </div>
  );
}
